<template>
  <div>
    <ts-loading-banner :loading="loading">
      <form class="form-horizontal">
        <fieldset>
          <legend>{{ $t('feePredefineItem.academic') }}</legend>
          <div class="row form-group">
            <div class="col-md-6">
              <div class="row">
                <label class="col-form-label col-md-4 required">{{ $t('feePredefineItem.mainProgram') }}</label>
                <div class="col-md-8">
                  <a-select style="width: 100%" v-model="model.acp_id" :options="main_programs" :show-search="true"
                    :filter-option="filterOption" @change="onMainProgramChange"></a-select>
                  <div v-if="errors.has('acp_id')">
                    <span class="text-danger">{{ errors.first('acp_id') }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="row">
                <label class="col-form-label col-md-4 required">{{ $t('feePredefineItem.subProgram') }}</label>
                <div class="col-md-8">
                  <ts-loading-banner :loading="main_program_loading">
                    <a-select style="width: 100%" v-model="model.acps_id" :options="sub_programs" :show-search="true"
                      :filter-option="filterOption" :disabled="!model.acp_id" @change="onSubProgramChange"></a-select>
                    <div v-if="errors.has('acps_id')">
                      <span class="text-danger">{{ errors.first('acps_id') }}</span>
                    </div>
                  </ts-loading-banner>
                </div>
              </div>
            </div>
          </div>

          <div class="row form-group">
            <div class="col-md-6">
              <div class="row">
                <label class="col-form-label col-md-4 required">{{ $t('feePredefineItem.level') }}</label>
                <div class="col-md-8">
                  <ts-loading-banner :loading="sub_program_loading">
                    <a-select style="width: 100%" v-model="model.level_id" :options="levels"
                      @change="onLevelChange"></a-select>
                    <div v-if="errors.has('level_id')">
                      <span class="text-danger">{{ errors.first('level_id') }}</span>
                    </div>
                  </ts-loading-banner>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="row">
                <label class="col-form-label col-md-4 required">{{ $t('feePredefineItem.grade') }}</label>
                <div class="col-md-8">
                  <ts-loading-banner :loading="level_loading">
                    <a-select style="width: 100%" v-model="model.grade_id" :options="grades"
                      @change="onGradeChange"></a-select>
                    <div v-if="errors.has('grade_id')">
                      <span class="text-danger">{{ errors.first('grade_id') }}</span>
                    </div>
                  </ts-loading-banner>
                </div>
              </div>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-md-6">
              <div class="row">
                <label class="col-form-label col-md-4 required">{{ $t('feePredefineItem.remak') }}</label>
                <div class="col-md-8">
                  <textarea v-model.trim="model.remark" rows="3" class="form-control"></textarea>
                  <div v-if="errors.has('remark')">
                    <span class="text-danger">{{ errors.first('remark') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </ts-loading-banner>

    <div class="row form-group mt-3">
      <div class="col-md-12">
        <h1 class="ts-text-lg ts-text-gray-600">{{ $t('feePredefineItem.itemList') }}</h1>
        <item-detail v-model="model" :validate="errors" :key="itemDetailKey" />
        <div v-if="errors.has('fee_define_detail_list')">
          <span class="text-danger">{{ errors.first('fee_define_detail_list') }}</span>
        </div>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-md-12 text-right">
        <ts-button @click="closeForm" type="default">{{ $t('cancel') }}</ts-button>
        <ts-button v-if="!editData" outline color="primary" class="ml-2 mr-2" :waiting="save_add_new_waiting"
          @click="saveAddNew">
          {{ $t('save_add_new') }}
        </ts-button>
        <ts-button v-if="!editData" :waiting="save_waiting" @click="save" color="primary">{{ $t('save') }}</ts-button>
        <ts-button v-else outline color="primary" class="ml-2 mr-2" :waiting="updating" @click="update">
          {{ $t('update') }}
        </ts-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ItemDetail from './item-detail'
import { Errors } from 'form-backend-validation'

export default {
  components: { ItemDetail },
  props: { editData: { type: Object, default: null } },
  data() {
    return {
      main_programs: [],
      sub_programs: [],
      levels: [],
      grades: [],
      main_program_loading: false,
      sub_program_loading: false,
      level_loading: false,
      loading: false,
      save_waiting: false,
      save_add_new_waiting: false,
      updating: false,
      errors: new Errors(),
      itemDetailKey: 0,
      model: {
        acp_id: null,
        acps_id: null,
        level_id: null,
        grade_id: null,
        remark: null,
        fee_define_detail_list: []
      }
    }
  },
  async mounted() {
    await this.fetchRescue()
    if (this.editData) await this.setData()
  },
  methods: {
    ...mapActions('feePredefineItem', ['getMainProgram', 'getSubProgram', 'getLevel', 'getGrade']),

    // Custom filter for search
    filterOption(input, option) {
      return option.label.toLowerCase().includes(input.toLowerCase())
    },

    async fetchRescue() {
      this.loading = true
      try {
        const response = await this.getMainProgram()
        this.main_programs = response.data.map(el => ({
          value: Number(el.acp_id),
          label: el.acp_name_foreign
        }))
        this.model.branch_id = Number(this.$store.state.authUser.default_branch_id)
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.loading = false
      }
    },

    async onMainProgramChange(acp_id) {
      this.main_program_loading = true
      this.model.acps_id = null
      this.model.level_id = null
      this.model.grade_id = null
      this.sub_programs = []
      this.levels = []
      this.grades = []

      try {
        const response = await this.getSubProgram({ filter: { acp_id: Number(acp_id) } })
        this.sub_programs = response.data.map(el => ({
          value: Number(el.acps_id),
          label: el.acps_name_foreign,
          scoring_cyles: el.scoring_cyles.map(sc => Number(sc.scoring_cycle_id))
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.main_program_loading = false
      }
    },

    async onSubProgramChange(acps_id) {
      if (!acps_id) return
      this.sub_program_loading = true
      this.model.level_id = null
      this.model.grade_id = null
      this.levels = []
      this.grades = []

      try {
        const response = await this.getLevel(Number(acps_id))
        this.levels = response.data.map(el => ({
          value: Number(el.level_id),
          label: el.level_name_foreign
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.sub_program_loading = false
      }
    },

    async onLevelChange(level_id) {
      if (!level_id) return
      this.level_loading = true
      this.model.grade_id = null
      this.grades = []

      try {
        const response = await this.getGrade(Number(level_id))
        this.grades = response.data.map(el => ({
          value: Number(el.grade_id),
          label: el.grade_name_foreign
        }))
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.level_loading = false
      }
    },

    onGradeChange(grade_id) {
      this.model.grade_id = grade_id
      this.model.fee_define_detail_list = []
      this.itemDetailKey++
    },

    async setData() {
      if (!this.editData) return
      this.loading = true
      try {
        await this.onMainProgramChange(Number(this.editData.acp_id))
        await this.onSubProgramChange(Number(this.editData.acps_id))
        await this.onLevelChange(Number(this.editData.level_id))

        this.model.id = this.editData.id
        this.model.acp_id = Number(this.editData.acp_id)
        this.model.acps_id = Number(this.editData.acps_id)
        this.model.level_id = Number(this.editData.level_id)
        this.model.grade_id = Number(this.editData.grade_id)
        this.model.remark = this.editData.remark || ''

        if (this.editData.details && this.editData.details.length) {
          this.model.fee_define_detail_list = this.editData.details.map(d => ({
            id: d.id,
            fee_type_id: d.fee_type_id,
            fee_setup_id: d.fee_setup_id || null,
            fee_name_en: d.fee_name_en || null,
            fee_description: d.fee_description || d.fee_name_en,
            quantity: d.quantity || 1
          }))
        }
        this.itemDetailKey++
      } catch (error) {
        this.$toasted.error(error.message)
      } finally {
        this.loading = false
      }
    },

    closeForm() {
      this.errors.clear()
      this.$emit('close')
    },

    resetInput() {
      this.model.acp_id = null
      this.model.acps_id = null
      this.model.level_id = null
      this.model.grade_id = null
      this.model.remark = ''
      this.model.fee_define_detail_list = []
      this.errors.clear()
      this.itemDetailKey++
    },

    async save() {
      this.save_waiting = true
      this.errors.clear()
      try {
        const response = await this.$store.dispatch('feePredefineItem/onSave', this.model)
        this.$toasted.success(response.data.message)
        this.$emit('saved')
        this.closeForm()
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = new Errors(error.response.data.errors)
          this.$toasted.error(this.$t('please_check_form_errors'))
        } else {
          this.$toasted.error(error.message || 'An error occurred')
        }
      } finally {
        this.save_waiting = false
      }
    },

    async saveAddNew() {
      this.save_add_new_waiting = true
      this.errors.clear()
      try {
        const response = await this.$store.dispatch('feePredefineItem/onSave', this.model)
        this.$toasted.success(response.data.message)
        this.$emit('saved')
        this.resetInput()
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = new Errors(error.response.data.errors)
          this.$toasted.error(this.$t('please_check_form_errors'))
        } else {
          this.$toasted.error(error.message || 'An error occurred')
        }
      } finally {
        this.save_add_new_waiting = false
      }
    },

    async update() {
      this.updating = true
      this.errors.clear()
      try {
        const response = await this.$store.dispatch('feePredefineItem/onUpdate', this.model)
        this.$toasted.success(response.data.message)
        this.$emit('saved')
        this.closeForm()
        this.resetInput()
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.errors = new Errors(error.response.data.errors)
          this.$toasted.error(this.$t('please_check_form_errors'))
        } else {
          this.$toasted.error(error.message || 'An error occurred')
        }
      } finally {
        this.updating = false
      }
    }
  }
}
</script>
